import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAddressComponent,
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxTimePipe,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { PointOfInterestHelper } from '../../../../../management-panel/points-of-interest/common/helpers/point-of-interest.helper';
import { OrderPointCategory } from '../../../../common/enums/order-point-category';
import { OrdersQueryParamKey } from '../../../../common/enums/orders-query-param-key';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type { Order } from '../../../../common/interfaces/order';
import type {
  OrderLoadingPoint,
  OrderUnloadingPoint,
} from '../../../../common/interfaces/order-point';

@Component({
  selector: 'app-order-modal-route-summary',
  imports: [
    OnyxDataWidgetComponent,
    TranslatePipe,
    OnyxAddressComponent,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    OnyxInformationHeadingComponent,
    OnyxDatePipe,
    OnyxTimePipe,
  ],
  templateUrl: './order-modal-route-summary.component.html',
  styleUrl: './order-modal-route-summary.component.scss',
})
export class OrderModalRouteSummaryComponent {
  private readonly orderHelper = inject(OrderHelper);
  private readonly pointOfInterestHelper = inject(PointOfInterestHelper);

  public readonly order = input.required<Order>();
  public readonly close$ = input.required<Subject<void>>();

  protected planRouteState = computed(() =>
    this.orderHelper.getPlanRouteState(this.order()),
  );

  protected loadingPoints = computed(() =>
    this.getPointsData(OrderPointCategory.LOADING),
  );
  protected unloadingPoints = computed(() =>
    this.getPointsData(OrderPointCategory.UNLOADING),
  );

  protected openPointModal(category: 'loading' | 'unloading'): void {
    const point =
      category === 'loading' ? this.loadingPoints() : this.unloadingPoints();
    const { address } = point.point;
    if (!('poiUuid' in address)) return;

    this.pointOfInterestHelper.openModal(address.poiUuid);
  }

  protected getPointsData<
    Category extends OrderPointCategory.LOADING | OrderPointCategory.UNLOADING,
    Point = Category extends OrderPointCategory.LOADING
      ? OrderLoadingPoint
      : OrderUnloadingPoint,
  >(
    category: Category,
  ): {
    point: Point;
    count: number;
  } {
    const points = this.order().points.filter(
      (point) => point.category === category,
    );

    if (category === OrderPointCategory.LOADING) {
      return { point: points[0] as Point, count: points.length };
    }
    return {
      point: points.at(-1) as Point,
      count: points.length,
    };
  }

  protected planRoute(): void {
    this.orderHelper.edit(this.order().uuid, {
      options: { close$: this.close$() },
      params: { [OrdersQueryParamKey.PLAN_ROUTE]: true },
    });
  }
}
